<template>
  <div class="register-box">
    <div class="register-content">
      <h1 class="title-register"><span class="visually-hidden">注册成为开发者</span></h1>

      <div
        v-if="step === 'form' || step === 'confirm'"
        class="register-steps"
      >
        <span :class="['step', { active: step === 'form' }]">1. 填写信息</span>
        <i class="step-line" />
        <span :class="['step', { active: step === 'confirm' }]">2. 确认信息</span>
      </div>

      <template v-if="step === 'form'">
        <p class="register-tip">
          请填写注册信息，带 * 的项目为必填项。
        </p>
        <el-form
          ref="registerForm"
          :model="formValue"
          :rules="rules"
          label-position="top"
          class="register-form"
          @submit.native.prevent="toConfirm"
        >
          <el-form-item
            label="账户名称"
            prop="username"
          >
            <el-input
              id="username"
              v-model.trim="formValue.username"
              maxlength="50"
              placeholder="请输入账户名称"
            />
          </el-form-item>
          <el-form-item
            label="公司名称"
            prop="company"
          >
            <el-input
              id="company"
              v-model.trim="formValue.company"
              maxlength="100"
              placeholder="请输入公司名称"
            />
          </el-form-item>
          <el-form-item
            label="统一社会信用代码"
            prop="license_code"
          >
            <el-input
              id="license_code"
              v-model.trim="formValue.license_code"
              maxlength="18"
              placeholder="请输入统一社会信用代码"
            />
          </el-form-item>
          <el-form-item
            label="联系人姓名"
            prop="contacter"
          >
            <el-input
              id="contacter"
              v-model.trim="formValue.contacter"
              maxlength="30"
              placeholder="请输入联系人姓名"
            />
          </el-form-item>
          <el-form-item
            label="手机号"
            prop="contacter_phone"
          >
            <el-input
              id="contacter_phone"
              v-model.trim="formValue.contacter_phone"
              maxlength="11"
              placeholder="请输入手机号"
            />
          </el-form-item>
          <el-form-item
            label="邮箱"
            prop="email"
          >
            <el-input
              id="email"
              v-model.trim="formValue.email"
              maxlength="100"
              placeholder="请输入邮箱"
            />
          </el-form-item>
          <el-form-item
            label="设置密码"
            prop="password"
          >
            <el-input
              id="password"
              v-model="formValue.password"
              type="password"
              maxlength="72"
              show-password
              autocomplete="new-password"
              placeholder="请输入登录密码，最多72字节"
            />
          </el-form-item>
          <el-form-item
            label="再次确认密码"
            prop="confirm_password"
          >
            <el-input
              id="confirm_password"
              v-model="formValue.confirm_password"
              type="password"
              maxlength="72"
              show-password
              autocomplete="new-password"
              placeholder="请再次输入登录密码"
            />
          </el-form-item>
        </el-form>
        <p class="register-notice">
          请确保填写信息的真实有效。
        </p>
        <div class="register-actions">
          <el-button @click="goLogin">返回登录</el-button>
          <el-button
            type="primary"
            :loading="submitting"
            @click="toConfirm"
          >
            下一步
          </el-button>
        </div>
      </template>

      <template v-else-if="step === 'confirm'">
        <p class="register-tip">请确认以下信息，确认后将创建开发者账号。</p>
        <div class="confirm-list">
          <div
            v-for="item in confirmItems"
            :key="item.key"
            class="confirm-item"
          >
            <span class="confirm-label">{{ item.label }}</span>
            <span class="confirm-value">{{ formValue[item.key] || '--' }}</span>
          </div>
        </div>
        <p class="register-notice">
          请注意核对信息。
        </p>
        <div class="register-actions">
          <el-button
            :disabled="submitting"
            @click="step = 'form'"
          >
            上一步
          </el-button>
          <el-button
            type="primary"
            :loading="submitting"
            @click="submit"
          >
            注册成为开发者
          </el-button>
        </div>
      </template>

      <template v-else-if="step === 'success'">
        <i class="icon-success" />
        <span class="response-title">注册成功</span>
        <span class="response-tip">恭喜您已成功注册为开发者</span>
        <el-button
          class="result-button"
          type="primary"
          @click="goLogin"
        >
          去登录<span v-if="countdown">（{{ countdown }}s）</span>
        </el-button>
      </template>

      <template v-else>
        <i class="icon-error" />
        <span class="response-title">注册失败</span>
        <span class="response-tip">{{ errorMessage }}</span>
        <div class="register-actions result-actions">
          <el-button @click="goLogin">返回登录</el-button>
          <el-button
            type="primary"
            @click="step = 'form'"
          >
            返回上一步
          </el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { registerDeveloper } from '@/service';

export default {
  data() {
    const validatePassword = (rule, value, callback) => {
      // Blob 按 UTF-8 编码计数，中文和表情不能按字符数判断字节上限。
      if (value && new Blob([value]).size > 72) {
        callback(new Error('密码最多72字节，中文通常占3字节，表情通常占4字节'));
      } else {
        callback();
      }
    };
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请再次输入登录密码'));
      } else if (value !== this.formValue.password) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    };

    return {
      step: 'form',
      submitting: false,
      disposed: false,
      countdown: 0,
      countdownTimer: null,
      errorMessage: '',
      formValue: {
        username: '',
        company: '',
        license_code: '',
        contacter: '',
        contacter_phone: '',
        email: '',
        password: '',
        confirm_password: '',
      },
      confirmItems: [
        { key: 'username', label: '账户名称' },
        { key: 'company', label: '公司名称' },
        { key: 'license_code', label: '统一社会信用代码' },
        { key: 'contacter', label: '联系人姓名' },
        { key: 'contacter_phone', label: '手机号' },
        { key: 'email', label: '邮箱' },
      ],
      rules: {
        username: [
          { required: true, message: '请输入账户名称', trigger: 'blur' },
        ],
        company: [
          { required: true, message: '请输入公司名称', trigger: 'blur' },
        ],
        license_code: [
          { required: true, message: '请输入统一社会信用代码', trigger: 'blur' },
          {
            pattern: /^[0-9A-Za-z]{18}$/,
            message: '请输入18位统一社会信用代码',
            trigger: 'blur',
          },
        ],
        contacter: [
          { required: true, message: '请输入联系人姓名', trigger: 'blur' },
        ],
        contacter_phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: '请输入正确的手机号',
            trigger: 'blur',
          },
        ],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          {
            type: 'email',
            message: '请输入正确的邮箱地址',
            trigger: 'blur',
          },
        ],
        password: [
          { required: true, message: '请输入登录密码', trigger: 'blur' },
          { validator: validatePassword, trigger: 'blur' },
        ],
        confirm_password: [
          { required: true, validator: validateConfirmPassword, trigger: 'blur' },
        ],
      },
    };
  },
  beforeDestroy() {
    this.disposed = true;
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    toConfirm() {
      if (this.submitting || this.step !== 'form') return;
      this.$refs.registerForm.validate((valid) => {
        if (valid) {
          this.step = 'confirm';
        }
      });
    },
    async submit() {
      if (this.submitting || this.step !== 'confirm') return;
      this.submitting = true;
      this.errorMessage = '';
      const payload = { ...this.formValue };
      delete payload.confirm_password;
      try {
        const res = await registerDeveloper(payload);
        if (this.disposed) return;
        if (res && res.code === 200) {
          this.formValue.password = '';
          this.formValue.confirm_password = '';
          this.step = 'success';
          this.startCountdown();
        } else {
          this.errorMessage = (res && res.message) || '注册失败，请稍后重试';
          this.step = 'error';
        }
      } catch (e) {
        if (this.disposed) return;
        if (e.code === 'ECONNABORTED') {
          this.errorMessage = '请求超时，请稍后重试；如已注册成功，可直接登录。';
        } else if (e.message === 'Network Error') {
          this.errorMessage = '网络连接失败，请检查网络后重试。';
        } else {
          this.errorMessage = e.message || '注册失败，请稍后重试';
        }
        this.step = 'error';
      } finally {
        this.submitting = false;
      }
    },
    startCountdown() {
      this.countdown = 3;
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.goLogin();
        }
      }, 1000);
    },
    goLogin() {
      this.$router.replace({ name: 'Login' });
    },
  },
};
</script>

<style lang="scss">
.mod-layout {
  &.page-register {
    .layout-header {
      .btn-link-box {
        position: relative;
        &.register {
          &::after {
            content: '';
            display: block;
            width: 100%;
            height: 8px;
            margin-top: -8px;
            background-color: #3f90f7;
            border-radius: 999px;
            z-index: 1;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.register-box {
  width: 100%;
  padding: 52px 0 80px;
}
.register-content {
  width: calc(100% - 40px);
  max-width: 800px;
  min-height: 600px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 42px 70px 50px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(16, 117, 229, 0.2);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.title-register {
  margin: 0;
  display: inline-block;
  width: 345px;
  height: 41px;
  background-image: url('./images/title_register.png');
  background-repeat: no-repeat;
  background-position: center;
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
.register-steps {
  display: flex;
  align-items: center;
  margin: 28px 0 18px;
  color: #c0c4cc;
  font-size: 14px;
  .step.active {
    color: $primary-color;
    font-weight: 500;
  }
  .step-line {
    width: 64px;
    height: 1px;
    margin: 0 14px;
    background: #dcdfe6;
  }
}
.register-tip {
  width: 100%;
  margin: 0 0 18px;
  color: #909399;
  font-size: 14px;
  text-align: center;
  line-height: 22px;
}
.register-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 32px;
  width: 100%;
  .el-form-item {
    margin-bottom: 14px;
  }
  ::v-deep .el-form-item__label {
    padding: 0;
    line-height: 24px;
    color: #606266;
  }
  ::v-deep .el-input__inner {
    height: 40px;
  }
}
.register-notice {
  width: 100%;
  margin: 2px 0 0;
  color: #ff9f43;
  font-size: 13px;
  line-height: 20px;
  text-align: center;
}
.register-actions {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 22px;
  .el-button {
    width: 180px;
    height: 40px;
    margin: 0 8px;
  }
}
.confirm-list {
  width: 100%;
  padding: 4px 24px;
  box-sizing: border-box;
  background: #f7f8fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.confirm-item {
  display: flex;
  min-height: 42px;
  align-items: center;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
  &:last-child {
    border-bottom: none;
  }
  .confirm-label {
    width: 145px;
    color: #909399;
  }
  .confirm-value {
    flex: 1;
    color: #303133;
    word-break: break-all;
  }
}
.icon-success,
.icon-error {
  display: inline-block;
  width: 72px;
  height: 72px;
  margin-top: 76px;
  background-repeat: no-repeat;
}
.icon-success {
  background-image: url('./images/icon_success.png');
}
.icon-error {
  background-image: url('./images/icon_error.png');
}
.response-title {
  margin-top: 24px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 24px;
  font-weight: bold;
  line-height: 35px;
}
.response-tip {
  margin-top: 14px;
  color: rgba(0, 0, 0, 0.4);
  font-size: 14px;
  line-height: 20px;
}
.result-button {
  width: 280px;
  height: 40px;
  margin-top: 28px;
}
.result-actions {
  margin-top: 28px;
}
@media (max-width: 640px) {
  .register-box { padding: 24px 0; }
  .register-content { padding: 32px 24px; }
  .register-form { grid-template-columns: 1fr; }
  .title-register { max-width: 100%; background-size: contain; }
  .confirm-item { align-items: flex-start; padding: 12px 0; }
  .confirm-label { flex-shrink: 0; }
}
</style>
